import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Server, Shield, Zap, Headphones, ChevronRight, Star, Clock } from "lucide-react";
import { Check } from "lucide-react";
import { RelatedServices } from "@/components/RelatedServices";
import { Globe, Monitor, Mail } from "lucide-react";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function WebHosting() {
  const { openDrawer } = useQuoteDrawer();

  const features = [
    {
      title: "High-Performance Servers",
      description: "SSD storage, optimized configurations, and global CDN for maximum speed.",
      icon: <Server className="h-8 w-8 text-blue-600" />
    },
    {
      title: "Advanced Security",
      description: "SSL certificates, DDoS protection, malware scanning, and regular backups.",
      icon: <Shield className="h-8 w-8 text-green-600" />
    },
    {
      title: "99.9% Uptime Guarantee",
      description: "Reliable hosting infrastructure with redundancy and monitoring systems.",
      icon: <Clock className="h-8 w-8 text-purple-600" />
    },
    {
      title: "24/7 Expert Support",
      description: "Round-the-clock technical support from hosting specialists.",
      icon: <Headphones className="h-8 w-8 text-purple-600" />
    }
  ];

  const plans = [
    {
      name: "Shared Hosting",
      price: "$9.99",
      period: "per month",
      features: ["10GB SSD Storage", "Unlimited Bandwidth", "Free SSL", "24/7 Support"],
      color: "from-blue-500 to-blue-600"
    },
    {
      name: "VPS Hosting",
      price: "$29.99",
      period: "per month",
      features: ["50GB SSD Storage", "Dedicated Resources", "Root Access", "Advanced Security"],
      popular: true,
      color: "from-purple-500 to-purple-600"
    },
    {
      name: "Dedicated Server",
      price: "$99.99",
      period: "per month",
      features: ["500GB SSD Storage", "Full Server Control", "Custom Config", "Priority Support"],
      color: "from-amber-500 to-amber-600"
    }
  ];

  return (
    <>
      <SEO
        title="Web Hosting Services UAE | Kecly"
        description="Reliable web hosting solutions in UAE with 99.9% uptime, SSD storage, and 24/7 support. Get your website online today."
        keywords="web hosting UAE, domain hosting Sharjah, VPS hosting Dubai"
      />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-32 px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
          </div>
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                Reliable <span className="text-blue-600">Web Hosting</span> Solutions
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Blazing fast hosting with 99.9% uptime guarantee and 24/7 expert support
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={openDrawer}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg"
                >
                  Get Started
                </Button>
                <Link href="/plans">
                  <Button 
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg"
                  >
                    View Plans
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-100">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Hosting Features</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Everything you need for a successful online presence</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-200 rounded-xl p-8 hover:shadow-lg hover:shadow-blue-500/20 transition-all"
                >
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Hosting Plans */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                <span className="text-blue-600">Hosting</span> Plans For Every Need
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Choose the perfect hosting solution for your website's requirements
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className={`group p-8 rounded-xl border-2 transition-all duration-300 ${plan.popular ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}
                >
                  {plan.popular && (
                    <div className="bg-blue-600 text-white text-sm font-medium py-1 px-3 rounded-full inline-block mb-4">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-6">{plan.price} {plan.period}</p>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-800 hover:bg-gray-700'} text-white`}
                  >
                    Get Started
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="Web Hosting" 
              services={[
                {
                  title: "Domain Registration",
                  description: "Secure your perfect domain name to use with your hosting.",
                  icon: <Globe className="h-8 w-8" />
                },
                {
                  title: "Web Design & Development",
                  description: "Build a professional website for your newly hosted domain.",
                  icon: <Monitor className="h-8 w-8" />
                },
                {
                  title: "Email Hosting",
                  description: "Professional email services with your custom domain.",
                  icon: <Mail className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Web Design Locations */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <WebDesignLocations />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-50">
          <div className="container mx-auto px-6 text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Ready to Launch Your Website?</h2>
              <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
                Get started with our reliable hosting solutions today
              </p>
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
                onClick={openDrawer}
              >
                Get Started Now
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
